import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
  ShieldExclamationIcon,
  LockOpenIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  StarIcon,
  EyeIcon,
  ArrowLeftIcon,
  PlusIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { adminAPI, User as APIUser } from '../../services/api';
import { usePaginatedTable } from '../../hooks/usePaginatedTable';
import { useUserDeletionImpact } from '../../hooks/useUserDeletionImpact';
import { useCurrency } from '../../contexts/CurrencyContext';
import Pagination from '../Common/Pagination';
import SuspendUserModal from './SuspendUserModal';
import AddUserModal from './AddUserModal';
import BulkUploadModal from './BulkUploadModal';

interface Customer {
  id: string;
  numeric_id?: number;
  name: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  is_approved?: boolean;
  email_verified?: boolean;
  phone_verified?: boolean;
  account_status?: 'active' | 'suspended' | 'banned';
  suspended_at?: string;
  suspension_expires_at?: string;
  suspension_reason?: string;
  average_rating?: number;
  total_ratings?: number;
  wallet_balance?: number;
  total_orders?: number;
  total_spent?: number;
}

const CustomerManagement: React.FC = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deletingCustomer, setDeletingCustomer] = useState<Customer | null>(null);
  const [suspendingCustomer, setSuspendingCustomer] = useState<Customer | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email_verified: false,
    phone_verified: false
  });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const filteredCustomers = customers.filter(customer => {
    const normalizedSearch = searchTerm.toLowerCase().replace(/[\s\-()]/g, '');
    const normalizedPhone = customer.phone?.replace(/[\s\-()]/g, '') || '';
    
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         normalizedPhone.includes(normalizedSearch);
    
    let matchesStatus = true;
    if (statusFilter === 'active') {
      matchesStatus = customer.account_status === 'active';
    } else if (statusFilter === 'suspended') {
      matchesStatus = customer.account_status === 'suspended';
    } else if (statusFilter === 'verified') {
      matchesStatus = customer.email_verified === true;
    } else if (statusFilter === 'unverified') {
      matchesStatus = customer.email_verified !== true;
    }
    
    return matchesSearch && matchesStatus;
  });

  const {
    paginatedItems,
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    goToPage,
    setItemsPerPage,
    resetPagination
  } = usePaginatedTable({
    items: filteredCustomers,
    initialItemsPerPage: 10
  });

  const {
    dependencies,
    loading: impactLoading,
    error: impactError,
    fetchDependencies,
    reset: resetImpact
  } = useUserDeletionImpact();

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    resetPagination();
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (deletingCustomer && deletingCustomer.numeric_id) {
      fetchDependencies(deletingCustomer.numeric_id);
    } else {
      resetImpact();
    }
  }, [deletingCustomer]);

  const fetchCustomers = async () => {
    try {
      const apiUsers = await adminAPI.getAllUsers();
      const customerUsers = apiUsers
        .filter((user: APIUser) => user.role === 'customer')
        .map((user: any) => ({
          ...user,
          id: user.id || user.uuid || '',
          numeric_id: typeof user.numeric_id === 'number' ? user.numeric_id : parseInt(user.id) || 0,
          name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown',
          role: user.role || 'customer',
          status: user.account_status || 'active',
          wallet_balance: user.wallet_balance || 0,
          total_orders: user.total_orders || 0,
          total_spent: user.total_spent || 0
        })) as Customer[];
      
      setCustomers(customerUsers);
    } catch (error) {
      console.error('Error fetching customers:', error);
      showNotification('error', 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setEditForm({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      phone: customer.phone || '',
      email_verified: customer.email_verified || false,
      phone_verified: customer.phone_verified || false
    });
  };

  const handleSaveEdit = async () => {
    if (!editingCustomer) return;
    
    try {
      const updatePayload = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        phone: editForm.phone,
        email_verified: editForm.email_verified,
        phone_verified: editForm.phone_verified
      };
      await adminAPI.updateUser(editingCustomer.numeric_id!, updatePayload);
      showNotification('success', 'Customer updated successfully');
      setEditingCustomer(null);
      fetchCustomers();
    } catch (error: any) {
      showNotification('error', error.response?.data?.detail || 'Failed to update customer');
    }
  };

  const handleDeleteCustomer = async () => {
    if (!deletingCustomer) return;
    
    try {
      await adminAPI.deleteUser(deletingCustomer.numeric_id!);
      showNotification('success', 'Customer deleted successfully');
      setDeletingCustomer(null);
      fetchCustomers();
    } catch (error: any) {
      showNotification('error', error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const handleUnsuspendCustomer = async (customer: Customer) => {
    try {
      await adminAPI.unsuspendUser(customer.numeric_id!, { note: 'Reinstated by admin', notify_user: true });
      showNotification('success', 'Customer unsuspended successfully');
      fetchCustomers();
    } catch (error: any) {
      showNotification('error', error.response?.data?.detail || 'Failed to unsuspend customer');
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          star <= Math.round(rating) ? (
            <StarIconSolid key={star} className="h-3 w-3 text-yellow-500" />
          ) : (
            <StarIcon key={star} className="h-3 w-3 text-gray-300" />
          )
        ))}
      </div>
    );
  };

  const getStatusBadge = (customer: Customer) => {
    if (customer.account_status === 'suspended') {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Suspended</span>;
    }
    
    const emailVerified = customer.email_verified || false;
    const phoneVerified = customer.phone_verified || false;
    
    if (emailVerified && phoneVerified) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Fully Verified</span>;
    }
    if (emailVerified) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Email Verified</span>;
    }
    if (phoneVerified) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">Phone Verified</span>;
    }
    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Unverified</span>;
  };

  const totalCustomers = customers.length;
  const activeCustomers = customers.filter(c => c.account_status === 'active').length;
  const fullyVerifiedCustomers = customers.filter(c => c.email_verified && c.phone_verified).length;
  const totalWalletBalance = customers.reduce((sum, c) => sum + (c.wallet_balance || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-volexx-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-poppins">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin-dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 font-poppins"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 font-poppins">Customer Management</h1>
              <p className="mt-2 text-gray-600 font-poppins">View and manage all platform customers</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBulkUploadModal(true)}
                className="inline-flex items-center px-4 py-2 border border-volexx-blue-600 text-volexx-blue-600 rounded-volexx font-semibold font-poppins hover:bg-volexx-blue-50 transition-colors"
              >
                <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                Bulk Upload
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 bg-volexx-blue-600 text-white rounded-volexx font-semibold font-poppins hover:bg-volexx-blue-700 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Customer
              </button>
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className={`mb-6 p-4 rounded-volexx-lg ${notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            <div className="flex items-center">
              {notification.type === 'success' ? (
                <CheckCircleIcon className="h-5 w-5 mr-2" />
              ) : (
                <XCircleIcon className="h-5 w-5 mr-2" />
              )}
              <span className="font-poppins">{notification.message}</span>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Total Customers</p>
                <p className="mt-2 text-3xl font-bold text-volexx-blue-600 font-poppins">{totalCustomers}</p>
              </div>
              <div className="bg-volexx-blue-100 rounded-volexx p-3">
                <UserIcon className="h-8 w-8 text-volexx-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Active Customers</p>
                <p className="mt-2 text-3xl font-bold text-green-600 font-poppins">{activeCustomers}</p>
              </div>
              <div className="bg-green-100 rounded-volexx p-3">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Fully Verified</p>
                <p className="mt-2 text-3xl font-bold text-purple-600 font-poppins">{fullyVerifiedCustomers}</p>
              </div>
              <div className="bg-purple-100 rounded-volexx p-3">
                <ShieldExclamationIcon className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Total Wallet Balance</p>
                <p className="mt-2 text-2xl font-bold text-orange-600 font-poppins">{formatPrice(totalWalletBalance)}</p>
              </div>
              <div className="bg-orange-100 rounded-volexx p-3">
                <CurrencyDollarIcon className="h-8 w-8 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
          </div>
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 font-poppins">
              Customers ({totalItems})
            </h2>
          </div>
          
          {paginatedItems.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <UserIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-900 font-poppins mb-2">No customers found</h3>
              <p className="text-gray-600 font-poppins">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Wallet
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Rating
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Joined
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedItems.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                                <span className="text-green-600 font-bold font-poppins">
                                  {(customer.first_name?.[0] || '') + (customer.last_name?.[0] || '')}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-bold text-gray-900 font-poppins">
                                {customer.first_name} {customer.last_name}
                              </div>
                              <div className="text-xs text-gray-500 font-poppins">ID: {customer.numeric_id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div className="flex items-center text-sm text-gray-900 font-poppins">
                              <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                              {customer.email}
                            </div>
                            <div className="flex items-center text-sm text-gray-500 font-poppins">
                              <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                              {customer.phone || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(customer)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900 font-poppins">
                            {formatPrice(customer.wallet_balance || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {customer.average_rating ? (
                              <>
                                {renderStars(customer.average_rating)}
                                <span className="ml-1 text-xs text-gray-500">
                                  ({customer.total_ratings || 0})
                                </span>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">No ratings</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-poppins">
                          {new Date(customer.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setSelectedCustomer(customer)}
                              className="p-2 text-volexx-blue-600 hover:bg-volexx-blue-50 rounded-volexx transition-colors"
                              title="View Details"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleEditCustomer(customer)}
                              className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-volexx transition-colors"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            {customer.account_status === 'suspended' ? (
                              <button
                                onClick={() => handleUnsuspendCustomer(customer)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-volexx transition-colors"
                                title="Unsuspend"
                              >
                                <LockOpenIcon className="h-5 w-5" />
                              </button>
                            ) : (
                              <button
                                onClick={() => setSuspendingCustomer(customer)}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-volexx transition-colors"
                                title="Suspend"
                              >
                                <ShieldExclamationIcon className="h-5 w-5" />
                              </button>
                            )}
                            <button
                              onClick={() => setDeletingCustomer(customer)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-volexx transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={goToPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </>
          )}
        </div>
      </div>

      {/* View Customer Details Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedCustomer(null)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-volexx-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 font-poppins">Customer Details</h3>
                  <button onClick={() => setSelectedCustomer(null)} className="text-gray-400 hover:text-gray-500">
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-center mb-6">
                    <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
                      <span className="text-green-600 font-bold text-2xl font-poppins">
                        {(selectedCustomer.first_name?.[0] || '') + (selectedCustomer.last_name?.[0] || '')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-volexx p-3">
                      <p className="text-xs text-gray-500 font-poppins">Full Name</p>
                      <p className="font-semibold text-gray-900 font-poppins">{selectedCustomer.first_name} {selectedCustomer.last_name}</p>
                    </div>
                    <div className="bg-gray-50 rounded-volexx p-3">
                      <p className="text-xs text-gray-500 font-poppins">Customer ID</p>
                      <p className="font-semibold text-gray-900 font-poppins">{selectedCustomer.numeric_id}</p>
                    </div>
                    <div className="bg-gray-50 rounded-volexx p-3">
                      <p className="text-xs text-gray-500 font-poppins">Email</p>
                      <p className="font-semibold text-gray-900 font-poppins text-sm">{selectedCustomer.email}</p>
                    </div>
                    <div className="bg-gray-50 rounded-volexx p-3">
                      <p className="text-xs text-gray-500 font-poppins">Phone</p>
                      <p className="font-semibold text-gray-900 font-poppins">{selectedCustomer.phone || 'N/A'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-volexx p-3">
                      <p className="text-xs text-gray-500 font-poppins">Wallet Balance</p>
                      <p className="font-semibold text-green-600 font-poppins">{formatPrice(selectedCustomer.wallet_balance || 0)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-volexx p-3">
                      <p className="text-xs text-gray-500 font-poppins">Status</p>
                      {getStatusBadge(selectedCustomer)}
                    </div>
                    <div className="bg-gray-50 rounded-volexx p-3">
                      <p className="text-xs text-gray-500 font-poppins">Rating</p>
                      <div className="flex items-center">
                        {selectedCustomer.average_rating ? (
                          <>
                            {renderStars(selectedCustomer.average_rating)}
                            <span className="ml-2 text-sm text-gray-600">{selectedCustomer.average_rating.toFixed(1)}</span>
                          </>
                        ) : (
                          <span className="text-sm text-gray-400">No ratings yet</span>
                        )}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-volexx p-3">
                      <p className="text-xs text-gray-500 font-poppins">Member Since</p>
                      <p className="font-semibold text-gray-900 font-poppins">{new Date(selectedCustomer.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {selectedCustomer.account_status === 'suspended' && (
                    <div className="bg-red-50 border border-red-200 rounded-volexx p-4 mt-4">
                      <p className="text-sm font-semibold text-red-800 font-poppins">Suspension Details</p>
                      <p className="text-sm text-red-600 font-poppins mt-1">
                        Reason: {selectedCustomer.suspension_reason || 'Not specified'}
                      </p>
                      {selectedCustomer.suspension_expires_at && (
                        <p className="text-sm text-red-600 font-poppins">
                          Expires: {new Date(selectedCustomer.suspension_expires_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="w-full inline-flex justify-center rounded-volexx border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm font-poppins"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {editingCustomer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setEditingCustomer(null)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-volexx-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-xl font-bold text-gray-900 font-poppins mb-6">Edit Customer</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 font-poppins mb-1">First Name</label>
                    <input
                      type="text"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                      className="w-full rounded-volexx border border-gray-300 px-3 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 font-poppins mb-1">Last Name</label>
                    <input
                      type="text"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                      className="w-full rounded-volexx border border-gray-300 px-3 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 font-poppins mb-1">Phone</label>
                    <input
                      type="text"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                      className="w-full rounded-volexx border border-gray-300 px-3 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                    />
                  </div>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editForm.email_verified}
                        onChange={(e) => setEditForm({...editForm, email_verified: e.target.checked})}
                        className="rounded border-gray-300 text-volexx-blue-600 focus:ring-volexx-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 font-poppins">Email Verified</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editForm.phone_verified}
                        onChange={(e) => setEditForm({...editForm, phone_verified: e.target.checked})}
                        className="rounded border-gray-300 text-volexx-blue-600 focus:ring-volexx-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 font-poppins">Phone Verified</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSaveEdit}
                  className="w-full inline-flex justify-center rounded-volexx border border-transparent shadow-sm px-4 py-2 bg-volexx-blue-600 text-base font-medium text-white hover:bg-volexx-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm font-poppins"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setEditingCustomer(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-volexx border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm font-poppins"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingCustomer && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setDeletingCustomer(null)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-volexx-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg font-bold text-gray-900 font-poppins">Delete Customer</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 font-poppins">
                        Are you sure you want to delete {deletingCustomer.first_name} {deletingCustomer.last_name}? 
                        This action cannot be undone.
                      </p>
                      {dependencies && (
                        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-volexx p-3">
                          <p className="text-sm font-semibold text-yellow-800 font-poppins">Impact Analysis:</p>
                          <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                            {dependencies.order_count > 0 && <li>{dependencies.order_count} orders will be affected</li>}
                            {dependencies.wallet_balance > 0 && <li>Wallet balance: {formatPrice(dependencies.wallet_balance)}</li>}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleDeleteCustomer}
                  className="w-full inline-flex justify-center rounded-volexx border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm font-poppins"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeletingCustomer(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-volexx border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm font-poppins"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suspend User Modal */}
      {suspendingCustomer && (
        <SuspendUserModal
          user={{
            id: String(suspendingCustomer.numeric_id),
            numeric_id: suspendingCustomer.numeric_id,
            name: `${suspendingCustomer.first_name || ''} ${suspendingCustomer.last_name || ''}`.trim(),
            email: suspendingCustomer.email,
            role: 'customer'
          }}
          onClose={() => setSuspendingCustomer(null)}
          onConfirm={async (data) => {
            try {
              await adminAPI.suspendUser(suspendingCustomer.numeric_id!, {
                suspension_type: data.suspension_type,
                reason: data.reason,
                duration_hours: data.duration_hours,
                notify_user: data.notify_user
              });
              setSuspendingCustomer(null);
              fetchCustomers();
              showNotification('success', 'Customer suspended successfully');
            } catch (error: any) {
              showNotification('error', error.response?.data?.detail || 'Failed to suspend customer');
            }
          }}
        />
      )}

      {/* Add Customer Modal */}
      {showAddModal && (
        <AddUserModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchCustomers();
            showNotification('success', 'Customer created successfully');
          }}
          defaultRole="customer"
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => {
            setShowBulkUploadModal(false);
            fetchCustomers();
            showNotification('success', 'Customers uploaded successfully');
          }}
          defaultRole="customer"
        />
      )}
    </div>
  );
};

export default CustomerManagement;
